import { z } from 'zod';
import {
  AuditSchema,
  DateOnlySchema,
  IdSchema,
  InstantSchema,
  VisibilitySchema,
} from './common.ts';
import { AssigneeSchema, ReminderSchema, type Assignee } from './schedule.ts';
import { RecordRefSchema } from './notification.ts';

/**
 * How urgent, in the four words the reference frames use.
 *
 * Four rather than a number, because a number invites a 1-to-10 scale that
 * nobody calibrates and everything ends up a 7.
 */
export const TODO_PRIORITIES = ['low', 'medium', 'high', 'highest'] as const;
export const TodoPrioritySchema = z.enum(TODO_PRIORITIES);
export type TodoPriority = z.infer<typeof TodoPrioritySchema>;

/** Where a due date is anchored on the schedule item it follows. */
export const DUE_ANCHORS = ['start', 'end'] as const;
export const DueAnchorSchema = z.enum(DUE_ANCHORS);
export type DueAnchor = z.infer<typeof DueAnchorSchema>;

/**
 * When a to-do is due.
 *
 * Either a date somebody chose, or a link to a schedule item with an
 * offset — "two days before framing starts". The second is the interesting
 * one: it follows the item, so a schedule that moves carries its to-dos
 * with it rather than leaving a list of dates that quietly became wrong.
 *
 * A discriminated union rather than nullable fields side by side, so "a
 * date and a link at the same time" is not a state the type permits.
 */
export const DueSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('date'),
    date: DateOnlySchema,
    /** `HH:mm`, local to the job. Absent means "that day", not midnight. */
    time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'A time looks like 14:30')
      .optional(),
  }),
  z.object({
    kind: z.literal('scheduleItem'),
    itemId: IdSchema,
    anchor: DueAnchorSchema,
    /**
     * Working days before (negative) or after (positive) the anchor.
     *
     * Working days, like every other count in this product. A person saying
     * "two days before framing starts" means two days they could work in,
     * and a due date that lands on a Sunday is a due date nobody meets.
     */
    offsetDays: z.number().int().min(-365).max(365).default(0),
  }),
]);
export type Due = z.infer<typeof DueSchema>;

/**
 * One row of a checklist.
 *
 * Order is the position in the array, not a field. The whole checklist is
 * one document field written atomically, so a reorder is a rewrite of the
 * array and cannot leave two rows claiming the same position — which is the
 * failure every `order: number` scheme eventually produces.
 */
export const ChecklistItemSchema = z.object({
  id: IdSchema,
  text: z.string().trim().min(1).max(500),
  done: z.boolean().default(false),
  doneAt: InstantSchema.optional(),
  doneBy: IdSchema.optional(),
  /** Per-row assignees, which the feature doc asks for. */
  assigneeIds: z.array(IdSchema).default([]),
});
export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;

/**
 * How many rows a checklist holds.
 *
 * Here rather than in `usecases/todos/checklist.ts`, where it used to
 * live, because `checklistTemplates` caps itself at the same number and a
 * domain entity must not import from a use case. A template that could
 * hold more rows than a to-do can is a template that fails on apply.
 *
 * The cap exists because a checklist is **one field on the to-do**: the
 * whole array is written together, so two people editing get a detected
 * conflict rather than a list that silently lost a row — and an unbounded
 * array in one document is a document that eventually will not write.
 */
export const MAX_CHECKLIST_ITEMS = 200;

/**
 * One to-do on one job.
 *
 * Job-scoped in the path, like daily logs and for the same reason: a `list`
 * rule can then enforce job assignment with one job read rather than one
 * per returned row. The cross-job list the feature doc asks for
 * (`docs/features/04-todos.md` §1) is a collection-group query, which is
 * its own task.
 */
export const TodoSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  title: z.string().trim().min(1).max(200),
  /** Plain text until there is a sanitiser, as for daily log notes. */
  notes: z.string().max(20_000).default(''),
  assignees: z.array(AssigneeSchema).default([]),
  /**
   * The ids from `assignees`, flattened.
   *
   * A query field, and the only reason it exists — the same reason as
   * `File.linkedKeys`. `hasAny` and `array-contains` need a scalar array:
   * matching `{kind, id, notify}` maps exactly would make "the to-dos
   * assigned to me" depend on whether `notify` happened to be true, and a
   * security rule cannot look inside an array of maps at all. The sub
   * access rule is written against this field, so it is load-bearing rather
   * than a convenience.
   *
   * Derived by `assigneeFields` and never written apart from `assignees`.
   */
  assigneeIds: z.array(IdSchema).default([]),
  due: DueSchema.optional(),
  priority: TodoPrioritySchema.default('medium'),
  tagIds: z.array(IdSchema).default([]),
  reminder: ReminderSchema.default('none'),
  visibility: VisibilitySchema,
  completed: z.boolean().default(false),
  completedAt: InstantSchema.optional(),
  completedBy: IdSchema.optional(),
  /**
   * What this to-do is about, in both directions.
   *
   * A daily log that spawned it, a schedule item it belongs to, a change
   * order it blocks. Held here as well as on the file side because these
   * are links between records rather than attachments: there is no third
   * document to hold them, so one of the two has to.
   */
  linkedRecords: z.array(RecordRefSchema).default([]),
  /** Query form of `linkedRecords`, derived — see `File.linkedKeys`. */
  linkedKeys: z.array(z.string().min(1)).default([]),
  /**
   * The checklist, in order.
   *
   * Empty means there is no checklist, which is different from a checklist
   * with nothing in it only in that nothing has to be shown. One field
   * rather than a subcollection: a checklist is read and written with its
   * to-do every time, and a subcollection would turn one read into N and
   * make "complete all" a batch instead of a field write.
   */
  checklist: z.array(ChecklistItemSchema).default([]),
});
export type Todo = z.infer<typeof TodoSchema>;

/** Whether a to-do is overdue on a given day. */
export function isOverdue(todo: Pick<Todo, 'completed' | 'due'>, today: string): boolean {
  if (todo.completed) return false;
  // A schedule-linked due date has to be resolved against the item first,
  // which needs the schedule; `resolveDue` does that and the caller passes
  // the result in as a plain date.
  if (todo.due?.kind !== 'date') return false;
  return todo.due.date < today;
}

/** The priority order a list sorts by: most urgent first. */
export function priorityRank(priority: TodoPriority): number {
  return TODO_PRIORITIES.length - 1 - TODO_PRIORITIES.indexOf(priority);
}

/** Both assignee fields, so the query form cannot fall behind the real one. */
export function assigneeFields(
  assignees: readonly Assignee[],
): Pick<Todo, 'assignees' | 'assigneeIds'> {
  return {
    assignees: [...assignees],
    // Deduplicated: somebody named twice is one person, and a rule using
    // `hasAny` does not care but a count of assignees would.
    assigneeIds: [...new Set(assignees.map((assignee) => assignee.id))],
  };
}

export interface ChecklistProgress {
  readonly done: number;
  readonly total: number;
}

export function checklistProgress(todo: Pick<Todo, 'checklist'>): ChecklistProgress {
  return {
    done: todo.checklist.filter((item) => item.done).length,
    total: todo.checklist.length,
  };
}

/**
 * Whether completing this to-do should ask first.
 *
 * `docs/features/04-todos.md` §3: completing a to-do with an incomplete
 * checklist asks to complete all. Asking rather than refusing, because the
 * person ticking it off knows something the checklist does not — but
 * silently marking a half-done checklist complete would make the progress
 * figure a lie, and the figure is the reason to keep a checklist at all.
 */
export function completingNeedsConfirmation(todo: Pick<Todo, 'completed' | 'checklist'>): boolean {
  if (todo.completed) return false;
  const { done, total } = checklistProgress(todo);
  return total > 0 && done < total;
}
